import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { calculateDiscountedPrice } from '../utils/discountUtils';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [deliverFees, setDeliverFees] = useState(0);
  const [cartOpen, setCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const clearCart = async () => {
    try {
      if (user) {
        await api.delete('/cart/clear');
      }
      setCart([]);
      setDeliverFees(0);
    } catch (err) {
      console.error('Error clearing cart:', err);
    }
  };

  const fetchCart = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const response = await api.get('/cart');
      setCart(response.data?.cart_items || response.data?.items || response.data);
      setDeliverFees(response.data?.delivery_value || response.data?.summary?.delivery_fee || 0);
    } catch (err) {
      console.error('Error fetching cart:', err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    if (!user) {
      window.location.href = '../login';
      return;
    }
    try {
      await api.post('/cart', { product_id: productId, quantity });
      await fetchCart();
    } catch (err) {
      console.error('Error adding to cart:', err);
    }
  };

  const updateQuantity = async (cartItemId, quantity) => {
    try {
      await api.put(`/cart/${cartItemId}`, { quantity });
      await fetchCart();
    } catch (err) {
      console.error('Error updating quantity:', err);
    }
  };

  const removeFromCart = async (cartItemId) => {
    try {
      await api.delete(`/cart/delete/${cartItemId}`);
      await fetchCart();
    } catch (err) {
      console.error('Error removing from cart:', err);
    }
  };

  // FIXED: Calculate product total first, then add delivery fees ONCE
  const productTotal = cart.reduce(
    (total, item) => total + (item.final_price || item.product?.price || 0) * item.quantity,
    0
  );

  const cartTotal = productTotal + deliverFees;

  // Calculate total savings from discounts
  const totalSavings = cart.reduce(
    (savings, item) => savings + (item.discount_applied || 0) * item.quantity,
    0
  );

  const cartCount = cart.reduce((count, item) => count + item.quantity, 0);

  // Get individual item price (with discount if applicable)
  const getItemPrice = (item) => {
    return item.final_price || item.product?.price || 0;
  };

  // Get original price before discount
  const getOriginalPrice = (item) => {
    return item.product?.price || 0;
  };

  // Check if item has discount
  const hasDiscount = (item) => {
    return item.discount_applied > 0 || (item.product?.discount_percent > 0);
  };

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setCart([]);
    }
  }, [user]);

  return (
    <CartContext.Provider
      value={{
        cart,
        cartOpen,
        setCartOpen,
        addToCart,
        updateQuantity,
        removeFromCart,
        cartTotal,
        productTotal, // Added for clarity
        totalSavings,
        deliverFees,
        cartCount,
        loading,
        clearCart,
        getItemPrice,
        getOriginalPrice,
        hasDiscount,
        fetchCart // Added for manual refresh
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);